import React, { useRef, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Paper, Button, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import * as dayjs from "dayjs";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { AlarmTemplateListDatatable } from "@/helpers/alarmTemplateListDatatable";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { FormItem } from "@/components/Form";
import AlarmRuleList from "@/components/FormFields/AlarmRuleList";
import TemplateStatus from "@/components/FormFields/TemplateStatus";
import TagList from "@/components/FormFields/TagList";
import DateRangePicker from "@/components/DateRangePicker";
import {
  deleteAlarmTemplateStatusApi,
  updateAlarmTemplateStatusApi
} from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import SubmitButton from "@/components/commons/SubmitButton";
import { IconFile } from "@/components/Icon";
import { useGlobalState } from "@/GlobalProvider";
import HasPermission from "@/components/commons/HasPermission";
import AlarmTemplateCreators from "@/components/FormFields/AlarmTemplateCreators";
import ViewDevicesModal from "./ViewDevicesModal";
import DeviceGroups from "@/components/FormFields/DeviceGroups/DeviceGroups";
import CollapsePanel, {
  CollapseItem,
  CollapseTrigger
} from "@/components/CollapsePanel";

const AlarmTemplateList = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const selectedIdsRef = useRef([]);
  const isReset = useRef(true);
  const operateType = useRef(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const viewDeviceId = useRef(null);
  const [viewDeviceModalOpen, setViewDeviceModalOpen] = useState(false);

  const initialValues = {
    rule: undefined,
    status: undefined,
    group: undefined,
    tag: undefined,
    creator: undefined,
    date: [null, null],
    keywords: ""
  };

  const history = useHistory();
  const { currentCompanyId } = useGlobalState();

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const date = formValues.date;
    return {
      alarm_policy_option_id: formValues.rule,
      status: formValues.status,
      group_ids: formValues.group,
      tag_ids: formValues.tag,
      create_user_id: formValues.creator,
      searching: formValues.keywords,
      start_time: date[0] ? dayjs.utc(date[0]).toISOString() : undefined,
      end_time: date[1] ? dayjs.utc(date[1]).toISOString() : undefined
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    handleReset();
    handleReloadDataTable(true);
  };

  const getSelectedIds = () => {
    const selectedIds = tableRef.current.getSelected();
    if (selectedIds && selectedIds.length) {
      selectedIdsRef.current = selectedIds;
      return selectedIds;
    }
    toast.info(t({ defaultMessage: "未选择任何模板" }));
  };

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const handleViewDevices = row => {
    viewDeviceId.current = row.id;
    setViewDeviceModalOpen(true);
  };

  const doEnable = async (ids, enable) => {
    try {
      setLoading(true);
      operateType.current = enable ? "enable" : "disable";
      if (!Array.isArray(ids)) {
        ids = [ids];
      }
      await updateAlarmTemplateStatusApi({
        status: enable ? 1 : 2,
        ids
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "操作成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = row => {
    modalConfirm({
      title: t({ defaultMessage: "禁用提醒" }),
      content: t(
        {
          defaultMessage:
            "是否确认禁用（{name}）该报警模板？禁用后新增报警设备将不能套用该模版。"
        },
        { name: row.name }
      ),
      onConfirm: () => {
        return doEnable(row.id, false);
      }
    });
  };

  const handleEnable = row => {
    modalConfirm({
      title: t({ defaultMessage: "启用提醒" }),
      content: t(
        {
          defaultMessage:
            "是否确认启用（{name}）该报警模板？启用后新增报警设备可以套用该模版。"
        },
        { name: row.name }
      ),
      onConfirm: () => {
        return doEnable(row.id, true);
      }
    });
  };

  const doDelete = async ids => {
    try {
      setLoading(true);
      operateType.current = "delete";
      if (!Array.isArray(ids)) {
        ids = [ids];
      }
      await deleteAlarmTemplateStatusApi({
        ids
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除提醒" }),
      content: t(
        {
          defaultMessage:
            "是否确认删除（{name}）该报警模板？删除后将同步取消设备，门店及标签对本模板的套用。"
        },
        { name: row.name }
      ),
      onConfirm: () => {
        return doDelete(row.id);
      }
    });
  };

  const handleBatchDisable = () => {
    if (getSelectedIds()) {
      modalConfirm({
        title: t({ defaultMessage: "提示" }),
        content: t({ defaultMessage: "确定要禁用吗？" }),
        onConfirm: () => {
          return doEnable(selectedIdsRef.current, false);
        }
      });
    }
  };

  const handleBatchEnable = () => {
    if (getSelectedIds()) {
      modalConfirm({
        title: t({ defaultMessage: "提示" }),
        content: t({ defaultMessage: "确定要启用吗？" }),
        onConfirm: () => {
          return doEnable(selectedIdsRef.current, true);
        }
      });
    }
  };

  const handleBatchDelete = () => {
    if (getSelectedIds()) {
      modalConfirm({
        title: t({ defaultMessage: "提示" }),
        content: t({ defaultMessage: "确定要删除吗？" }),
        onConfirm: () => {
          return doDelete(selectedIdsRef.current);
        }
      });
    }
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]);

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableHeader}>
            <div className={classes.tableTop}>
              <div>
                <Typography variant="h5" component="span">
                  {t({ defaultMessage: "报警模板列表" })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  className={classes.subTitle}
                >
                  <IconFile size={15} />
                  {t({ defaultMessage: "{num}条" }, { num: total })}
                </Typography>
              </div>
              <div className={classes.rightAction}>
                <SearchInput
                  placeholder={t({ defaultMessage: "搜索" })}
                  name="keywords"
                  icon={null}
                  value={values.keywords}
                  onChange={handleChange}
                />
                <HasPermission permission="alarm_policy_create">
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      history.push("/alarm/template/create");
                    }}
                  >
                    {t({ defaultMessage: "添加报警模板" })}
                  </Button>
                </HasPermission>
              </div>
            </div>
            <CollapsePanel>
              <div className={classes.tableQueryForm}>
                <FormItem
                  label={t({ defaultMessage: "报警规则" })}
                  className={classes.formItem}
                >
                  <AlarmRuleList
                    name="rule"
                    value={values.rule}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "模板状态" })}
                  className={classes.formItem}
                >
                  <TemplateStatus
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "门店" })}
                  className={classes.formItem}
                >
                  <DeviceGroups
                    name="group"
                    value={values.group}
                    onChange={handleChange}
                  />
                </FormItem>
                <CollapseItem>
                  <FormItem
                    label={t({ defaultMessage: "创建人" })}
                    className={classes.formItem}
                  >
                    <AlarmTemplateCreators
                      name="creator"
                      value={values.creator}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "创建时间" })}
                    className={classes.formItem}
                  >
                    <DateRangePicker
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                    />
                  </FormItem>
                </CollapseItem>
                <FormItem className={classes.formItem}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ marginRight: 8 }}
                  >
                    {t({ defaultMessage: "查询" })}
                  </Button>
                  <Button variant="outlined" onClick={resetQuery}>
                    {t({ defaultMessage: "重置" })}
                  </Button>
                  <CollapseTrigger />
                </FormItem>
              </div>
            </CollapsePanel>
          </div>
        </form>
        <AlarmTemplateListDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onView={data => {
            history.push(`/alarm/template/info/${data.id}`);
          }}
          onEdit={data => {
            history.push(`/alarm/template/edit/${data.id}`);
          }}
          onViewDevices={handleViewDevices}
          onDisable={handleDisable}
          onEnable={handleEnable}
          onDelete={handleDelete}
          onLoaded={handleLoaded}
          footer={
            <>
              <HasPermission permission="alarm_policy_enable">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "disable"}
                  onClick={handleBatchDisable}
                >
                  {t({ defaultMessage: "批量禁用" })}
                </SubmitButton>
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "enable"}
                  onClick={handleBatchEnable}
                >
                  {t({ defaultMessage: "批量启用" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="alarm_policy_delete">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && operateType.current === "delete"}
                  onClick={handleBatchDelete}
                >
                  {t({ defaultMessage: "批量删除" })}
                </SubmitButton>
              </HasPermission>
            </>
          }
        />
      </Paper>
      <ViewDevicesModal
        open={viewDeviceModalOpen}
        templateId={viewDeviceId.current}
        onClose={() => setViewDeviceModalOpen(false)}
      />
    </div>
  );
};

export default AlarmTemplateList;
